import { inject, Injectable } from '@angular/core';

import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import {
    SelectedEntitiesByLayerId,
    SelectedEntity,
} from '@connect-services/client/services/map/selection/selection-state.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { SourcesService } from '@connect-services/client/services/sources.service';

interface HighlightGroup {
    entityIds: string[];
    entityType: LayerStyleTypeEnum;
    sourceId: string;
}

interface HighlightedSourceState {
    source: MapBaseSource<unknown>;
    types: Set<LayerStyleTypeEnum>;
}

type HighlightUuidSet = Set<string>;
type HighlightByEntityType = Map<LayerStyleTypeEnum, HighlightUuidSet>;
type HighlightIndex = Map<string, HighlightByEntityType>;
type HighlightedSourcesByType = Map<string, HighlightedSourceState>;

@Injectable({
    providedIn: 'root',
})
export class MapSelectionHighlightService {
    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly highlightedSourcesByType: HighlightedSourcesByType = new Map();
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly sourcesService = inject(SourcesService);

    public clearFieldInterventionHighlight(): void {
        const layerIds: string[] = Object.keys(this.createFieldInterventionService.selectedEntities());

        this.createFieldInterventionService.currentSources().forEach((source: MapBaseSource<unknown>) => {
            if (!layerIds.includes(source.sourceId)) {
                return;
            }

            this.mapHighlightEntityService.resetHighlight(source, this.createFieldInterventionService.entityType());
        });
    }

    public clearSourcesHighlight(sourceIds: string[]): void {
        const sources = this.sourcesService
            .sources()
            .filter((source: MapBaseSource<unknown>) => sourceIds.includes(source.sourceId));

        sources.forEach((source: MapBaseSource<unknown>) =>
            this.mapHighlightEntityService.resetHighlight(source, LayerStyleTypeEnum.FILL),
        );
    }

    public async synchronizeHighlights(selectedEntitiesByLayerId: SelectedEntitiesByLayerId): Promise<void> {
        const highlightGroups: HighlightGroup[] = this.buildHighlightGroupsFromSelection(selectedEntitiesByLayerId);

        const nextHighlightedSourcesByType: HighlightedSourcesByType =
            this.buildHighlightedSourcesByType(highlightGroups);

        await this.resetRemovedHighlights(nextHighlightedSourcesByType);

        highlightGroups.forEach((group: HighlightGroup) => {
            this.applyHighlightGroup(group);
        });

        this.highlightedSourcesByType.clear();

        nextHighlightedSourcesByType.forEach((state: HighlightedSourceState, sourceId: string) => {
            this.highlightedSourcesByType.set(sourceId, state);
        });
    }

    private applyHighlightGroup(group: HighlightGroup): void {
        if (!group.entityIds.length) {
            return;
        }

        const source: MapBaseSource<unknown> | undefined = this.sourcesService
            .sources()
            .find((item: MapBaseSource<unknown>) => item.sourceId === group.sourceId);

        if (!source) {
            return;
        }

        this.mapHighlightEntityService.manageHighlightFeature(source, group.entityIds, group.entityType);
    }

    private buildHighlightedSourcesByType(highlightGroups: HighlightGroup[]): HighlightedSourcesByType {
        const highlightedSourcesByType: HighlightedSourcesByType = new Map();

        highlightGroups.forEach((group: HighlightGroup) => {
            const source = this.sourcesService
                .sources()
                .find((item: MapBaseSource<unknown>) => item.sourceId === group.sourceId);

            if (!source) {
                return;
            }

            const existingState = highlightedSourcesByType.get(group.sourceId);

            if (existingState) {
                existingState.types.add(group.entityType);

                return;
            }

            highlightedSourcesByType.set(group.sourceId, {
                source,
                types: new Set<LayerStyleTypeEnum>([group.entityType]),
            });
        });

        return highlightedSourcesByType;
    }

    private buildHighlightGroupsFromIndex(highlightIndex: HighlightIndex): HighlightGroup[] {
        const groups: HighlightGroup[] = [];

        highlightIndex.forEach((highlightByEntityType: HighlightByEntityType, sourceId: string) => {
            highlightByEntityType.forEach((uuidSet: HighlightUuidSet, entityType: LayerStyleTypeEnum) => {
                if (!uuidSet.size) {
                    return;
                }
                const entityIds: string[] = Array.from(uuidSet);

                groups.push({
                    entityIds,
                    entityType,
                    sourceId,
                });
            });
        });

        return groups;
    }

    private buildHighlightGroupsFromSelection(selectedEntitiesByLayerId: SelectedEntitiesByLayerId): HighlightGroup[] {
        const highlightIndex: HighlightIndex = new Map();

        Object.entries(selectedEntitiesByLayerId).forEach(([sourceId, entities]: [string, SelectedEntity[]]) => {
            entities
                .filter(
                    (entity: SelectedEntity): entity is SelectedEntity & { styleType: LayerStyleTypeEnum } =>
                        !!entity.styleType && !!entity.uuid && entity.isVisible,
                )
                .forEach((entity: SelectedEntity & { styleType: LayerStyleTypeEnum }) => {
                    const entityType: LayerStyleTypeEnum = entity.styleType;
                    const entityByType: HighlightByEntityType = this.getOrCreateEntityTypeMap(highlightIndex, sourceId);

                    this.getOrCreateUuidSet(entityByType, entityType).add(entity.uuid);
                });
        });

        return this.buildHighlightGroupsFromIndex(highlightIndex);
    }

    private getOrCreateEntityTypeMap(highlightIndex: HighlightIndex, sourceId: string): HighlightByEntityType {
        const existingHighlightByType = highlightIndex.get(sourceId);

        if (existingHighlightByType) {
            return existingHighlightByType;
        }

        const newHighlightByType: HighlightByEntityType = new Map();
        highlightIndex.set(sourceId, newHighlightByType);

        return newHighlightByType;
    }

    private getOrCreateUuidSet(entityTypeToUuids: HighlightByEntityType, entityType: LayerStyleTypeEnum): Set<string> {
        const existingUuidSet = entityTypeToUuids.get(entityType);

        if (existingUuidSet) {
            return existingUuidSet;
        }

        const newUuidSet: HighlightUuidSet = new Set();
        entityTypeToUuids.set(entityType, newUuidSet);

        return newUuidSet;
    }

    private async resetRemovedHighlights(nextHighlightedSourcesByType: HighlightedSourcesByType): Promise<void> {
        for (const [sourceId, currentState] of this.highlightedSourcesByType) {
            const nextState = nextHighlightedSourcesByType.get(sourceId);

            for (const entityType of currentState.types) {
                if (nextState?.types.has(entityType)) {
                    continue;
                }

                await this.mapHighlightEntityService.resetHighlightAfterMapReady(currentState.source, entityType);
            }
        }
    }
}
